"use client";

import React from 'react';
import Icon from '@/components/ui/AppIcon';

const comparisonData = [
  {
    feature: "Response Time",
    helium: "< 2 hours",
    typical: "24-48 hours (if at all)",
    icon: "ClockIcon"
  },
  {
    feature: "Team Location",
    helium: "European-operated in Shenzhen",
    typical: "Local agents only",
    icon: "GlobeAltIcon"
  },
  {
    feature: "Communication",
    helium: "Native English, German, French",
    typical: "Broken English, mistranslations",
    icon: "ChatBubbleLeftRightIcon"
  },
  {
    feature: "Technology",
    helium: "Real-time dashboard & API",
    typical: "Email & spreadsheets",
    icon: "ComputerDesktopIcon"
  },
  {
    feature: "Sourcing",
    helium: "Factory-direct with QC",
    typical: "Middleman markups",
    icon: "BuildingOfficeIcon"
  },
  {
    feature: "Transparency",
    helium: "Full cost breakdown",
    typical: "Hidden fees",
    icon: "EyeIcon"
  }
];

export default function ComparisonSection() {
  return (
    <section className="relative py-16 md:py-24 bg-secondary-dark overflow-hidden">
      {/* Background glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl h-96 pointer-events-none opacity-10"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(91, 46, 255, 0.6) 0%, transparent 70%)",
          filter: "blur(60px)"
        }}
      />
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-text-primary mb-3 md:mb-4">
            Helium vs Typical China 3PL
          </h2>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto px-4">
            See why brands choose Helium for premium China fulfilment
          </p>
        </div>

        {/* Comparison grid */}
        <div className="max-w-5xl mx-auto">
          {/* Header row - hidden on mobile */}
          <div className="hidden md:grid grid-cols-3 gap-4 mb-4 px-6">
            <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide">Feature</div>
            <div className="text-sm font-semibold text-accent-primary uppercase tracking-wide text-center">Helium</div>
            <div className="text-sm font-semibold text-text-secondary uppercase tracking-wide text-center">Typical 3PL</div>
          </div>

          {/* Comparison rows */}
          <div className="space-y-3 md:space-y-3">
            {comparisonData?.map((item, index) => (
              <div 
                key={index}
                className="glass-card p-4 md:p-6 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Mobile layout - stacked */}
                <div className="md:hidden space-y-3">
                  {/* Feature */}
                  <div className="flex items-center gap-3 pb-3 border-b border-glass-border">
                    <div className="w-8 h-8 rounded-lg bg-glass-white border border-glass-border flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={18} className="text-accent-primary" />
                    </div>
                    <span className="font-semibold text-text-primary text-sm">{item?.feature}</span>
                  </div>
                  
                  {/* Helium */}
                  <div>
                    <div className="text-xs font-semibold text-accent-primary uppercase tracking-wide mb-2">Helium</div>
                    <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-primary/10 border border-accent-primary/20 text-text-primary font-medium text-sm">
                      {item?.helium}
                    </span>
                  </div>
                  
                  {/* Typical */}
                  <div>
                    <div className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">Typical 3PL</div>
                    <span className="text-text-secondary font-medium text-sm">{item?.typical}</span>
                  </div>
                </div>

                {/* Desktop layout - grid */}
                <div className="hidden md:grid grid-cols-3 gap-4 items-center">
                  {/* Feature */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-glass-white border border-glass-border flex items-center justify-center flex-shrink-0">
                      <Icon name={item?.icon} size={20} className="text-accent-primary" />
                    </div>
                    <span className="font-semibold text-text-primary">{item?.feature}</span>
                  </div>

                  {/* Helium */}
                  <div className="text-center">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-accent-primary/10 border border-accent-primary/20 text-text-primary font-medium">
                      {item?.helium}
                    </span>
                  </div>

                  {/* Typical */}
                  <div className="text-center">
                    <span className="text-text-secondary font-medium">{item?.typical}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}